<script setup lang="ts">
import { computed, PropType, Ref, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import { toolkitUrls, urlHelpers } from '@/util/links.js'
import { useRouter } from 'vue-router'
import { useRouterStore } from '@/router/router.store'
import { useContentAccessStore } from '@/content_access/content_access.store'
import {
  ActionPermissions,
  LimitActionButtons,
  limitActionButtons,
  limitActionMessage,
  limitActionTitle
} from '@/components/limit_action_copy'

const router = useRouter()
const routerStore = useRouterStore()
const { lastNonPremiumPageVisited } = storeToRefs(routerStore)

const props = defineProps({
  fallbackRouteName: {
    type: String,
    default: 'AllFolders'
  },
  externalOpen: {
    type: Boolean,
    default: false
  },
  bodyMessage: {
    type: String,
    default: 'You are not authorized to perform this action.'
  },
  actionPermissions: {
    type: Object as PropType<ActionPermissions | null>,
    default: null
  }
})

const emit = defineEmits(['update:externalOpen'])
const visibleRef: Ref<boolean> = ref(props.externalOpen)

const header = computed(() =>
  props.actionPermissions ? limitActionTitle(props.actionPermissions) : 'Feature available with premium membership'
)
const message = computed(() =>
  props.actionPermissions ? limitActionMessage(props.actionPermissions) : props.bodyMessage
)
const buttons = computed<LimitActionButtons>(() =>
  props.actionPermissions ? limitActionButtons(props.actionPermissions) : 'plans'
)

type BlockedContentInfo = Parameters<
  ReturnType<typeof useContentAccessStore>['sendContentAccessBlockedQuota']
>[0]['contentInfo']
const sendQuotaBlocked = function (permissions: ActionPermissions): void {
  useContentAccessStore().sendContentAccessBlockedQuota({
    actionType: permissions.actionType,
    contentInfo: {
      cu: permissions.contentUuid,
      ct: permissions.contentTitle,
      ctype: permissions.contentType
    } as BlockedContentInfo
  })
}

const closeModal = function (): void {
  visibleRef.value = false
  emit('update:externalOpen', false)
}

watch(
  () => props.externalOpen,
  () => {
    visibleRef.value = props.externalOpen
    if (props.externalOpen && props.actionPermissions?.blockQuota) sendQuotaBlocked(props.actionPermissions)
  }
)

watch(
  () => visibleRef.value,
  () => {
    emit('update:externalOpen', visibleRef.value)
  }
)

const openSubscribeLink = function (): void {
  window.open(toolkitUrls.subscribe, '_blank')
}

const redirectTo = function (url: string): void {
  window.location.href = url
}
const signIn = () => redirectTo(urlHelpers.signInReturn(window.location.href))
const signUp = () => redirectTo(urlHelpers.signUpReturn('toolkit', window.location.href))

const dismiss = function (): void {
  if (props.actionPermissions) closeModal()
  else goBack()
}

const goBack = function (): void {
  closeModal()
  if (lastNonPremiumPageVisited.value) {
    router.push(lastNonPremiumPageVisited.value)
  } else {
    router.push({ name: props.fallbackRouteName })
  }
}
</script>

<template>
  <GbDialog
    dialogId="premium-action-modal"
    v-model:visible="visibleRef"
    :header="header"
    :footer-buttons="buttons !== 'none'"
    @hide="dismiss"
  >
    <template #bodyContent>
      <div class="flex flex-col gap-2">
        <p>{{ message }}</p>
      </div>
    </template>
    <template #footerButtonLeft>
      <template v-if="buttons === 'auth'">
        <GbButton type="button" gbTheme="bluePrimary" label="Sign in" @click="signIn"></GbButton>
        <GbButton type="button" gbTheme="grayOutlineSecondary" label="Create an account" @click="signUp"></GbButton>
      </template>
      <GbButton
        v-if="buttons === 'plans'"
        type="button"
        gbTheme="bluePrimary"
        label="Request a quote"
        @click="openSubscribeLink"
      ></GbButton>
    </template>
    <template #footerButtonRight>
      <GbButton v-if="buttons === 'plans'" type="button" gbTheme="grayOutlineSecondary" label="OK" @click="dismiss"></GbButton>
      <GbButton v-if="buttons === 'ok'" type="button" gbTheme="bluePrimary" label="OK" @click="dismiss"></GbButton>
    </template>
  </GbDialog>
</template>
